<template>
  <div class="simulation-container">
    <img class="page-header-image" :src="baseUrl + 'header.png'" alt="header" />

    <div class="config-panel">
      <h3 style="margin-bottom: 20px;text-shadow:none">📋 物流线参数配置</h3>
      <div class="form-grid">
        <div class="form-item">
          <label>格子长度 a (米):</label>
          <input type="number" v-model.number="config.cellLength" min="0.1" step="0.1" :disabled="isRunning" />
        </div>
        <div class="form-item">
          <label>下料格子数 b (个):</label>
          <input type="number" v-model.number="config.loadingCellCount" min="2" step="1" :disabled="isRunning" />
        </div>
        <div class="form-item">
          <label>间隔普通格子数 c (个):</label>
          <input type="number" v-model.number="config.gapCellCount" min="0" step="1" :disabled="isRunning" />
        </div>
        <div class="form-item">
          <label>运动速度 v (m/s):</label>
          <input type="number" v-model.number="config.speed" min="0.1" step="0.1" :disabled="isRunning" />
        </div>
        <div class="form-item">
          <label>下料周期 T (秒):</label>
          <input type="number" v-model.number="config.cycleTime" min="0.5" step="0.1" :disabled="isRunning" />
        </div>
        <div class="form-item">
          <label>最大缓存数 (个):</label>
          <input type="number" v-model.number="config.maxBufferLimit" min="1" step="1" :disabled="isRunning" />
        </div>
      </div>
      <div class="controls">
        <div class="stats">
          总格子数: {{ totalCells }} | 物流线总长度: {{ totalLength.toFixed(2) }} 米 | 单格运动耗时: {{ transferTimeMs.toFixed(0) }}ms
          <span class="warning-text">为了不造成无限积压，速度 v 必须满足：v ≥ 1.2(a * b) / T​ 也就是（ {{ minRequiredSpeed.toFixed(2) }} ）m/s</span>
        </div>
        <button @click="startSimulation" v-if="!isRunning" class="btn-start">开始运行</button>
        <button @click="stopSimulation" v-if="isRunning" class="btn-stop">停止运行</button>
        <button @click="resetSimulation" class="btn-reset">重置</button>
      </div>
    </div>

    <div class="conveyor-viewport">
      <div class="conveyor-track" :style="conveyorTrackStyle">
        <div v-for="(cell, index) in cells" :key="cell.id" class="cell-container">
          <div v-if="cell.type === 'loading'" class="machine-info">
            <div class="machine-timer" :style="{ width: getMachineProgress(cell.id) * 100 + '%' }"></div>
            <span class="buffer-count" :class="{ 'has-buffer': getMachineBuffer(cell.id) > 0, 'at-limit': getMachineBuffer(cell.id) >= config.maxBufferLimit }">
              缓存: {{ getMachineBuffer(cell.id) }}/{{ config.maxBufferLimit }}（历史最大: {{ getMachineMaxBuffer(cell.id) }}）
            </span>
          </div>

          <div class="cell" :class="[cell.type, { 'is-busy': cell.isBusy }]">
            <span class="cell-id">#{{ index + 1 }}</span>
            <div v-if="cell.hasPallet" class="pallet" :class="{ 'animating-out': cell.isAnimatingOut }">📦</div>
          </div>

          <!-- 下料口机器图片（逆时针 180°），居中对准格子下方 -->
          <img
            v-if="cell.type === 'loading'"
            :src="machineImg"
            alt="machine"
            class="machine-image"
          />

          <div v-if="index < cells.length - 1" class="connector"></div>
        </div>
      </div>
    </div>

    <div class="legend">
      图例:
      <span class="legend-item loading">IsLoading 下料格子</span>
      <span class="legend-item transfer">IsTransfer 普通格子</span>
      <span class="legend-item busy">Busy (滚轮转动中)</span>
    </div>

    <footer class="page-footer">
      赢合科技-研发工程中心-叠片&组装产品中心-软件部
    </footer>
  </div>
</template>

<script setup>
import { reactive, computed, ref, onUnmounted, watch } from "vue";
import machineImg from "./1.jpg";
const baseUrl = process.env.BASE_URL || "/";

// --- 1. 状态管理 (State Management) ---

// 配置参数
const config = reactive({
  cellLength: 1.2, // a米
  loadingCellCount: 10, // b个
  gapCellCount: 7, // c个
  speed: 1.1, // v m/s
  cycleTime: 39.2, // T秒
  maxBufferLimit: 3 // 最大缓存数
});

const isRunning = ref(false);
const cells = reactive([]);
// 使用 Map 方便通过格子 ID 查找对应的机器
const machines = reactive(new Map());

let animationFrameId = null;
let lastFrameTime = 0;

// --- 2. 计算属性 (Computed) ---

// 计算总格子数
const totalCells = computed(() => {
  const b = config.loadingCellCount;
  const c = config.gapCellCount;
  if (b < 2) return b;
  return b + (b - 1) * c;
});

// 计算物流线总长度（米）
const totalLength = computed(() => totalCells.value * config.cellLength);

// 计算物料经过一个格子所需的物理时间 (毫秒)
const transferTimeMs = computed(() => (config.cellLength / config.speed) * 1000);

// 不造成积压的最低速度 v ≥ (a * b) / T
const minRequiredSpeed = computed(() => (config.cellLength * config.loadingCellCount) / config.cycleTime * 1.2);

// 计算 conveyor-track 的样式对象
const conveyorTrackStyle = computed(() => {
  return {
    '--transfer-time': `${transferTimeMs.value}ms`
  };
});

// --- 3. 初始化与控制逻辑 ---

// 初始化物流线结构
const initConveyorSystem = () => {
  cells.length = 0;
  machines.clear();

  let cellIdCounter = 0;
  const b = config.loadingCellCount;
  const c = config.gapCellCount;

  for (let i = 0; i < b; i++) {
    // 1. 添加一个下料格子
    const loadingCell = {
      id: cellIdCounter++,
      type: "loading",
      hasPallet: false,
      isBusy: false,
      isAnimatingOut: false
    };
    cells.push(loadingCell);
    // 初始化对应的机器
    machines.set(loadingCell.id, {
      cellId: loadingCell.id,
      buffer: 0,
      maxBuffer: 0,
      timeSinceLastSpawn: 0,
      maxBufferLimit: config.maxBufferLimit
    });

    // 2. 如果不是最后一个下料格子，在后面添加 c 个普通格子
    if (i < b - 1) {
      for (let j = 0; j < c; j++) {
        cells.push({
          id: cellIdCounter++,
          type: "transfer",
          hasPallet: false,
          isBusy: false,
          isAnimatingOut: false
        });
      }
    }
  }
};

// 当配置参数变更且仿真未运行时，自动重建格子结构
watch(
  () => [config.loadingCellCount, config.gapCellCount, config.cellLength, config.speed, config.cycleTime],
  () => {
    if (!isRunning.value) {
      initConveyorSystem();
    }
  }
);

// 当最大缓存数变更时，更新所有机器的 maxBufferLimit
watch(
  () => config.maxBufferLimit,
  (newLimit) => {
    machines.forEach(machine => {
      machine.maxBufferLimit = newLimit;
    });
  }
);

const startSimulation = () => {
  if (cells.length === 0) initConveyorSystem();
  isRunning.value = true;
  lastFrameTime = performance.now();
  gameLoop(lastFrameTime);
};

const stopSimulation = () => {
  isRunning.value = false;
  if (animationFrameId) cancelAnimationFrame(animationFrameId);
};

const resetSimulation = () => {
  stopSimulation();
  initConveyorSystem();
};

// --- 4. 核心仿真循环 (Simulation Loop) ---

const gameLoop = (timestamp) => {
  if (!isRunning.value) return;

  // 计算两帧之间的时间差 (秒)，用于精确计时
  const deltaTimeStr = (timestamp - lastFrameTime) / 1000;
  lastFrameTime = timestamp;

  // 先执行物流线移动，再执行下料，确保物流线上的货物优先移动
  updateConveyorMovement();
  updateMachines(deltaTimeStr);

  animationFrameId = requestAnimationFrame(gameLoop);
};

// 更新机器状态 (生产与补货)
const updateMachines = (dt) => {
  machines.forEach(machine => {
    // 检查缓存是否达到上限
    const isAtLimit = machine.buffer >= machine.maxBufferLimit;
    
    // 只有在缓存未达到上限时才更新计时器
    if (!isAtLimit) {
      // 更新生产计时器
      machine.timeSinceLastSpawn += dt;

      // 达到周期 T，尝试生产
      if (machine.timeSinceLastSpawn >= config.cycleTime) {
        machine.timeSinceLastSpawn = 0; // 重置计时器
        machine.buffer++; // 先放入缓存
        // 更新历史最大缓存数
        if (machine.buffer > machine.maxBuffer) {
          machine.maxBuffer = machine.buffer;
        }
      }
    }
    // 如果达到上限，计时器不更新（保持当前值，但不继续累加）

    // 尝试从缓存往格子上放料
    const targetCell = cells.find(c => c.id === machine.cellId);
    if (targetCell && machine.buffer > 0) {
      // 关键条件：格子必须是空的，且当前没有处于忙碌状态
      if (!targetCell.hasPallet && !targetCell.isBusy) {
        // 检查前一个格子是否有货物准备移动到这个下料口
        const targetCellIndex = cells.findIndex(c => c.id === machine.cellId);
        let hasIncomingPallet = false;
        
        if (targetCellIndex > 0) {
          const prevCell = cells[targetCellIndex - 1];
          // 检查前一个格子是否有货且可以移动（不忙碌）
          // 如果前一个格子有货且不忙碌，说明它可能在这一帧移动过来
          // 由于 updateConveyorMovement 先执行，如果前一个格子要移动，
          // 目标格子（当前下料口）应该已经被标记为 isBusy
          // 但为了确保优先级，我们额外检查前一个格子的状态
          if (prevCell.hasPallet && !prevCell.isBusy) {
            // 前一个格子有货且不忙碌，优先让物流线上的货物移动
            hasIncomingPallet = true;
          }
        }
        
        // 如果没有前一个格子的货物准备移动过来，才让缓存中的料下到格子上
        if (!hasIncomingPallet) {
          targetCell.hasPallet = true;
          machine.buffer--;
          // 当缓存减少后，如果之前达到上限，现在可以继续计时了
          // 计时器会自动继续更新（因为 isAtLimit 变为 false）
        }
      }
    }
  });
};

// 更新物流线运动 (积放逻辑核心)
const updateConveyorMovement = () => {
  // 重要：从后往前遍历！
  // 如果从前往后遍历，一个料可能会在同一帧内连续跳过多个空格子，这是不符合物理规律的。
  // 从后往前遍历能确保每一帧每个料最多只移动一格，体现“积放”效果。
  for (let i = cells.length - 1; i >= 0; i--) {
    const currentCell = cells[i];

    // 如果当前格子没货，或者已经在忙着运货了，跳过
    if (!currentCell.hasPallet || currentCell.isBusy) continue;

    // 判断是否是最后一个格子
    if (i === cells.length - 1) {
      // 最后一个格子直接移出屏幕
      startMoveProcess(i, null);
    } else {
      const nextCell = cells[i + 1];
      // 积放核心条件：后一个格子必须为空，且后一个格子也不能处于忙碌状态
      if (!nextCell.hasPallet && !nextCell.isBusy) {
        startMoveProcess(i, i + 1);
      }
    }
  }
};

// 开始一个移动过程 (包含逻辑锁定和动画触发)
const startMoveProcess = (fromIndex, toIndex) => {
  const fromCell = cells[fromIndex];
  const toCell = toIndex !== null ? cells[toIndex] : null;

  // 1. 锁定相关格子，防止重复触发
  fromCell.isBusy = true;
  if (toCell) toCell.isBusy = true;

  // 2. 触发 CSS 动画 (使托盘滑向下个格子)
  // 使用 setTimeout 让浏览器先渲染 isBusy 状态，再添加 animating-out 类以触发 transition
  setTimeout(() => {
    fromCell.isAnimatingOut = true;
  }, 20);

  // 3. 设置定时器，在物理耗时结束后更新逻辑状态
  setTimeout(() => {
    // 动画和物理过程结束
    fromCell.hasPallet = false;
    fromCell.isBusy = false;
    fromCell.isAnimatingOut = false;

    if (toCell) {
      toCell.hasPallet = true;
      toCell.isBusy = false;
    } else {
      // 移出了末端，货物消失
    }
  }, transferTimeMs.value);
};

// --- 辅助函数 ---
const getMachineBuffer = (cellId) => {
  return machines.get(cellId)?.buffer || 0;
};
const getMachineMaxBuffer = (cellId) => {
  return machines.get(cellId)?.maxBuffer || 0;
};
const getMachineProgress = (cellId) => {
  const m = machines.get(cellId);
  if (!m) return 0;
  // 返回 0~1 之间的进度值，用于显示进度条
  return Math.min(m.timeSinceLastSpawn / config.cycleTime, 1.0);
};

// 生命周期钩子
onUnmounted(() => {
  stopSimulation();
});

// 初始运行一次以显示空架子
initConveyorSystem();
</script>

<style scoped>
.close-button{
  display: none;
}
.simulation-container {
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  max-width: 97%;
  margin: 0 auto;
  padding: 20px;
  background-color: #f4f6f9;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

/* --- 配置面板样式 --- */
.config-panel {
  background: white;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}
.config-panel h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #383030;
}
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}
.form-item {
  display: flex;
  flex-direction: column;
}
.form-item label {
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
}
.form-item input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
}
.form-item input:disabled {
  background-color: #eee;
  cursor: not-allowed;
}
.controls {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}
.stats {
  flex-grow: 1;
  font-size: 14px;
  color: #888;
}
button {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}
.btn-start {
  background-color: #4caf50;
  color: white;
}
.btn-start:hover {
  background-color: #45a049;
}
.btn-stop {
  background-color: #f44336;
  color: white;
}
.btn-stop:hover {
  background-color: #d32f2f;
}
.btn-reset {
  background-color: #9e9e9e;
  color: white;
}
.btn-reset:hover {
  background-color: #757575;
}

/* --- 物流线可视区样式 --- */
.conveyor-viewport {
  overflow-x: auto;
  padding: 80px 30px 360px 60px;
  background: white;
  border-radius: 8px;
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.05);
}

.conveyor-track {
  display: flex;
  align-items: center;
  /* 定义一个 CSS 变量供动画使用 */
  --transfer-time: 1000ms;
}

.cell-container {
  display: flex;
  align-items: center;
  position: relative;
  width: 105%;
}

/* 机器信息顶部悬浮条 */
.machine-info {
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  font-size: 13px;
  white-space: nowrap;
  overflow: visible;
  min-width: 100%;
}
.machine-timer {
  height: 4px;
  background-color: #4caf50;
  width: 0%;
  transition: width 0.1s linear; /* 平滑进度条 */
  border-radius: 2px;
  margin-bottom: 4px;
}
.buffer-count {
  display: inline-block;
  padding: 2px 6px;
  background: #eee;
  border-radius: 10px;
  color: #666;
  font-weight: bold;
  white-space: nowrap;
  position: relative;
  /* top: -51px; */
}
.buffer-count.has-buffer {
  background: #ff9800;
  color: white;
}
.buffer-count.at-limit {
  background: #f44336;
  color: white;
  font-weight: bold;
}

.warning-text {
  color: #f44336;
  margin-left: 8px;
  font-weight: bold;
}

/* 下方机器细标记：垂直细线对准格子中心 */
.machine-below-marker {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 2px;
  height: 16px;
  background: #2196f3;
  bottom: -20px;
  border-radius: 1px;
}

/* 下料口机器图片：放在格子下方居中，逆时针 180° */
.machine-image {
  position: absolute;
  left: 50%;
  top: calc(100% + 16px);
  width: 125px;
  transform: translateX(-76%) rotate(-180deg);
  pointer-events: none;
  user-select: none;
}

/* 格子本体样式 */
.cell {
  width: 60px;
  height: 60px;
  border: 3px solid #ccc;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f9f9f9;
  /* 模拟滚轮背景 */
  background-image: repeating-linear-gradient(90deg, transparent, transparent 5px, rgba(0, 0, 0, 0.05) 5px, rgba(0, 0, 0, 0.05) 10px);
  transition: border-color 0.3s;
}

.cell.loading {
  border-color: #2196f3; /* 蓝色表示下料口 */
}
.cell.transfer {
  border-color: #9e9e9e; /* 灰色表示普通口 */
}
.cell.is-busy {
  border-color: #ffc107; /* 黄色表示忙碌/转动中 */
  background-image: repeating-linear-gradient(90deg, transparent, transparent 5px, rgba(255, 193, 7, 0.1) 5px, rgba(255, 193, 7, 0.1) 10px);
  /* 添加一个简单的转动动画效果 */
  animation: roller-move 0.5s linear infinite;
}

@keyframes roller-move {
  from {
    background-position: 0 0;
  }
  to {
    background-position: 10px 0;
  }
}

.cell-id {
  position: absolute;
  bottom: -20px;
  font-size: 11px;
  color: #999;
  width: 100%;
  text-align: center;
}

/* 连接器样式 */
.connector {
  width: 10px;
  height: 10px;
  background-color: #ddd;
  border-radius: 50%;
  margin: 0 2px;
}

/* 托盘样式与动画 */
.pallet {
  width: 40px;
  height: 40px;
  background-color: #ff5722;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 2;
  /* 核心动画：当 animating-out 类被添加时，向右平移 100% + 间距 */
  transition: transform var(--transfer-time) linear;
}

.pallet.animating-out {
  /* 移动距离 = 格子宽度(60) + 边框(6) + 连接器区域(14) = 约80px，这里用百分比简化近似 */
  /* transform: translateX(calc(100% + 14px)); */
  /* 移动距离 = 格子宽度(60) + 边框(6) + 连接器/间距约 14px ≈ 80px */
  transform: translateX(80px);
}

.legend {
  margin-top: 20px;
  font-size: 14px;
  color: #666;
  display: flex;
  gap: 15px;
  justify-content: center;
}
.legend-item {
  display: flex;
  align-items: center;
}
.legend-item::before {
  content: "";
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-right: 5px;
  border: 2px solid;
}
.legend-item.loading::before {
  border-color: #2196f3;
}
.legend-item.transfer::before {
  border-color: #9e9e9e;
}
.legend-item.busy::before {
  border-color: #ffc107;
  background: #ffc107;
}

.page-footer {
  margin-top: 50px;
  text-align: center;
  font-size: 14px;
  color: #777;
}

/* 隐藏 dat.GUI 默认的 “Open Controls” 按钮和悬浮控件 */
:global(.dg .close-button) {
  display: none !important;
}
:global(.dg.ac) {
  display: none !important;
}

/* 头部图片 */
.page-header-image {
  display: block;
  margin: 0 auto 16px auto;
  max-width: 300px;
  width: 100%;
  object-fit: contain;
}
</style>
